import {useCallback, useMemo, useState} from 'react'
import {getInputRequests} from '@github-ui/ahp-relay/ahp-session-selectors'
import type {ChatInputRequest} from '@microsoft/agent-host-protocol'
import {
  ahpCompletionFromResponse,
  ahpInputRequestToPrompt,
} from '@github-ui/copilot-shared-ui/ahp/ahp-prompt-conversion'
import {useOptionalEstablishedAhpSession} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session'
import {useOptionalEstablishedAhpSessionViewState} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session-view'
import type {NormalizedPrompt} from '@github-ui/copilot-shared-ui/ahp/interactive-prompt-types'
import type {CommandType} from '@github-ui/copilot-shared-ui/ahp/command-types'

export interface UseAhpPromptStateOptions {
  /** Task id the shared relay is keyed by. */
  taskId: string | undefined
  /** MC environment the session is bound to. */
  environmentId: string | undefined
  /** Mission Control session id the relay observes. */
  sessionId: string | undefined
  /** When false the hook is inert (no relay acquisition, no pending prompt). */
  enabled: boolean
}

export interface AhpPromptState {
  /** The current pending prompt, or null if none. */
  pendingPrompt: NormalizedPrompt | null
  /** Zero-based index of the current pending prompt. */
  pendingPromptIndex: number
  /** Total number of unresolved prompts. */
  pendingPromptCount: number
  /** True before the first relay snapshot arrives. */
  isLoading: boolean
  /** True when the AHP connection is observe-only and cannot answer prompts. */
  passive: boolean
  /** Not applicable for AHP prompts (the relay owns the lifecycle). */
  isExpired: boolean
  /** Optimistically resolve a request id so it hides before the server confirms. */
  markPromptResolved: (requestId: string) => void
  /** Roll back an optimistic resolution so the prompt re-appears on failure. */
  unmarkPromptResolved: (requestId: string) => void
  /** Dismiss the current prompt client-side. */
  dismissPrompt: () => void
  /** Select the previous unresolved prompt, if one exists. */
  goToPreviousPrompt: () => void
  /** Select the next unresolved prompt, if one exists. */
  goToNextPrompt: () => void
  /**
   * Answer the pending prompt by dispatching `chat/inputCompleted` over the
   * relay. Accepts the same `(toolCallId, response, commandType)` payload the
   * shared `PromptInput` components emit, so the composer wiring is identical to
   * the events/logs path.
   */
  respond: (toolCallId: string, response: unknown, commandType: CommandType) => void
}

/**
 * AHP-native source for the interactive prompt composer. Reads pending
 * {@link ChatInputRequest}s from the shared relay's `ChatState` and projects
 * them onto {@link NormalizedPrompt}s, mirroring the `EventsPromptState` shape
 * so it can be swapped in wherever the events/logs source is used.
 */
export function useAhpPromptState({enabled}: UseAhpPromptStateOptions): AhpPromptState {
  const establishedSession = useOptionalEstablishedAhpSession()
  const {state, passive} = useOptionalEstablishedAhpSessionViewState()
  const [resolvedRequestIds, setResolvedRequestIds] = useState<Set<string>>(() => new Set())
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null)

  const requestsById = useMemo(() => {
    const map = new Map<string, ChatInputRequest>()
    if (state) for (const request of getInputRequests(state)) map.set(request.id, request)
    return map
  }, [state])

  const pendingPrompts = useMemo(() => {
    if (!state) return []
    const prompts: NormalizedPrompt[] = []
    for (const request of getInputRequests(state)) {
      if (resolvedRequestIds.has(request.id)) continue
      const prompt = ahpInputRequestToPrompt(request)
      if (prompt) prompts.push(prompt)
    }
    return prompts
  }, [state, resolvedRequestIds])

  const isLoading = enabled && !state

  const selectedIndex = useMemo(() => {
    if (!pendingPrompts.length) return -1
    if (!selectedRequestId) return 0
    const index = pendingPrompts.findIndex(prompt => prompt.toolCallId === selectedRequestId)
    return index === -1 ? 0 : index
  }, [pendingPrompts, selectedRequestId])

  const visiblePendingPrompt = isLoading ? null : (pendingPrompts[selectedIndex] ?? null)

  const markPromptResolved = useCallback((requestId: string) => {
    setResolvedRequestIds(prev => new Set(prev).add(requestId))
  }, [])

  const unmarkPromptResolved = useCallback((requestId: string) => {
    setResolvedRequestIds(prev => {
      if (!prev.has(requestId)) return prev
      const next = new Set(prev)
      next.delete(requestId)
      return next
    })
  }, [])

  const dismissPrompt = useCallback(() => {
    if (visiblePendingPrompt) {
      setResolvedRequestIds(prev => new Set(prev).add(visiblePendingPrompt.toolCallId))
    }
  }, [visiblePendingPrompt])

  const goToPreviousPrompt = useCallback(() => {
    setSelectedRequestId(prev => {
      const currentIndex = prev ? pendingPrompts.findIndex(p => p.toolCallId === prev) : selectedIndex
      const nextIndex = Math.max(0, currentIndex - 1)
      return pendingPrompts[nextIndex]?.toolCallId ?? null
    })
  }, [pendingPrompts, selectedIndex])

  const goToNextPrompt = useCallback(() => {
    setSelectedRequestId(prev => {
      const currentIndex = prev ? pendingPrompts.findIndex(p => p.toolCallId === prev) : selectedIndex
      const nextIndex = Math.min(pendingPrompts.length - 1, currentIndex + 1)
      return pendingPrompts[nextIndex]?.toolCallId ?? null
    })
  }, [pendingPrompts, selectedIndex])

  const respond = useCallback(
    (toolCallId: string, response: unknown, commandType: CommandType) => {
      if (passive) throw new Error('relay connection is read-only')
      // ask_user / elicitation: answer with chat/inputCompleted.
      const request = requestsById.get(toolCallId)
      if (!request) return
      const params = ahpCompletionFromResponse(request, response, commandType)
      if (!params) return
      if (!establishedSession) {
        throw new Error('An established AHP session is required to respond to this prompt.')
      }
      establishedSession.commands.respondToPrompt(params)
    },
    [establishedSession, passive, requestsById],
  )

  return {
    pendingPrompt: visiblePendingPrompt,
    pendingPromptIndex: visiblePendingPrompt ? selectedIndex : -1,
    pendingPromptCount: pendingPrompts.length,
    isLoading,
    passive,
    isExpired: false,
    markPromptResolved,
    unmarkPromptResolved,
    dismissPrompt,
    goToPreviousPrompt,
    goToNextPrompt,
    respond,
  }
}
